import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import produce from "immer"
import _ from "lodash"
import { collapsedCardName, FieldName, userSettingsKey } from "../common/text"
import { handleGetSelectionSliceInitValues } from "./Support"

export enum FlightTableTab {
  condensed,
  default,
  spacious
}

export enum ArrDepSorting {
  Arr = "Arr",
  Dep = "Dep",
  ArrDep = "Arr+Dep"
}

export interface ISelectionState {
  airportSeasonsSelector: IAirportSeasonsSelection
  flightSelector: IFlightSelection
  slotAvailabilitySelector: ISlotAvailabilitySelection
  pagesHistory: IPagesHistory
  collapsedCards: ICollapsedCardsSelection
  userSettings: IUserSettingsSelection
  flightTableSelection: IFlightTableSelection
}

export interface IAirportSeasonsSelection {
  [FieldName.season]: string
  [FieldName.countries]: string[]
  [FieldName.airports]: string[]
  [FieldName.useLocalTime]: boolean
  [FieldName.useICAOCode]: boolean
}

export type IFieldValues = {
  [key in FieldName]?: any
}

export interface IUserSettingsSelection {
  [userSettingsKey.showOptionalFields]: boolean
  [userSettingsKey.useSSIMView]: boolean
  [userSettingsKey.pinSelectorPanel]: boolean
}

export interface IFlightTableSelection {
  chosenFlightTableTab: FlightTableTab
  chosenArrDepSorting: ArrDepSorting
}

export interface ISlotAvailabilitySelection {
  values: IFieldValues
}

export interface IFlightSelection {
  values: IFieldValues
}

export interface IUserSettingsValue {
  field: userSettingsKey
  value: any
}

export interface IFieldValue {
  field: FieldName
  value: any
}

export interface IPagesHistory {
  [pageId: string]: IPageSelectionHistory
}

export interface IPageSelectionHistory {
  history: IFieldValues[]
}

export interface IPageFieldValuesPayload {
  page: string
  values: IFieldValues
}

export interface ICollapsedCardValue {
  field: collapsedCardName
  value: any
}

export type ICollapsedCardsSelection = {
  [key in collapsedCardName]: boolean
}

const selectionSlice = createSlice({
  name: "selection",
  initialState: handleGetSelectionSliceInitValues(),
  reducers: {
    updateAirportSeasonsSelection: produce(
      (draft: ISelectionState, action: PayloadAction<IFieldValue>) => {
        draft.airportSeasonsSelector[action.payload.field] =
          action.payload.value
      }
    ),

    updateFlightSelection: produce(
      (draft: ISelectionState, action: PayloadAction<IFieldValue>) => {
        draft.flightSelector.values[action.payload.field] = action.payload.value
      }
    ),

    updateSlotAvailabilitySelection: produce(
      (draft: ISelectionState, action: PayloadAction<IFieldValue>) => {
        draft.slotAvailabilitySelector.values[action.payload.field] =
          action.payload.value
      }
    ),

    pushHistory: produce(
      (
        draft: ISelectionState,
        action: PayloadAction<IPageFieldValuesPayload>
      ) => {
        if (draft.pagesHistory == null) {
          draft.pagesHistory = {}
        }

        let pagesHistory = draft.pagesHistory[action.payload.page]
        let values = _.omitBy(action.payload.values, (v) => {
          return v == null
        })

        if (pagesHistory == null) {
          pagesHistory = {
            history: [values]
          }
        } else {
          let newHistory = _.cloneDeep(pagesHistory.history)
          newHistory = newHistory.filter((h) => !_.isEqual(values, h))
          newHistory.push(values)

          if (newHistory.length > 10) {
            newHistory.shift()
          }

          pagesHistory = {
            history: newHistory
          }
        }

        draft.pagesHistory[action.payload.page] = pagesHistory
      }
    ),

    updateCollapsedCardsSelection: produce(
      (draft: ISelectionState, action: PayloadAction<ICollapsedCardValue>) => {
        if (!draft.collapsedCards) {
          draft.collapsedCards = {
            flightID: false,
            period: false,
            times: true,
            other: true,
            handling: false,
            checkboxes: true
          }
        }
        draft.collapsedCards[action.payload.field] = action.payload.value
      }
    ),

    updateUserSettingsSelection: produce(
      (draft: ISelectionState, action: PayloadAction<IUserSettingsValue>) => {
        if (!draft.userSettings) {
          draft.userSettings = {
            showOptionalFields: true,
            useSSIMView: true,
            pinSelectorPanel: false
          }
        }
        draft.userSettings[action.payload.field] = action.payload.value
      }
    ),

    updateChosenFlighTableTabSelection: produce(
      (draft: ISelectionState, action: PayloadAction<FlightTableTab>) => {
        draft.flightTableSelection.chosenFlightTableTab = action.payload
      }
    ),

    updateChosenArrDepSortingSelection: produce(
      (draft: ISelectionState, action: PayloadAction<ArrDepSorting>) => {
        draft.flightTableSelection.chosenArrDepSorting = action.payload
      }
    ),

    updateSelectionSlice: produce(
      (draft: ISelectionState, action: PayloadAction<ISelectionState>) => {
        // draft = action.payload
        draft.airportSeasonsSelector = action.payload.airportSeasonsSelector
        draft.flightSelector = action.payload.flightSelector
        draft.slotAvailabilitySelector = action.payload.slotAvailabilitySelector
        draft.pagesHistory = action.payload.pagesHistory
        draft.collapsedCards = action.payload.collapsedCards
        draft.userSettings = action.payload.userSettings
        draft.flightTableSelection = action.payload.flightTableSelection
      }
    )
  }
})

export const {
  updateChosenFlighTableTabSelection,
  updateChosenArrDepSortingSelection,
  updateSlotAvailabilitySelection,
  updateAirportSeasonsSelection,
  updateCollapsedCardsSelection,
  updateUserSettingsSelection,
  updateFlightSelection,
  updateSelectionSlice,
  pushHistory
} = selectionSlice.actions
export default selectionSlice.reducer // "Ducks" - quack!
